import { useEffect, useState } from 'react'

import { useQuery } from '@tanstack/react-query'

import i18n from './i18n'
import css from './index.module.css'

import { Buttons } from '#/local/component/Button'
import { ProductList as EshopProductList } from '#/local/component/Eshop/ProductList'
import { Gift } from '#/local/component/InfoBox/Gift'
import { SeasonPoints } from '#/local/component/InfoBox/SeasonPoints'
import { RentalGuarantee } from '#/local/component/Rental/Guarantee'
import { ProductList as RentalProductList } from '#/local/component/Rental/ProductList'

import { Modal } from '#/global/component/Modal'
import type { AddToCartEshopAfterParams } from '#/global/component/Product/AddToCart/Eshop'
import type { AddToCartRentalAfterParams, AddToCartRentalParams } from '#/global/component/Product/AddToCart/Rental'
import { ProductPreviewLine } from '#/global/component/Product/ProductPreviewLine'

import { ServiceProduct } from '#/global/service/Product'

import { Config } from '#/global/helper/Config'
import { init as initDataLayer } from '#/global/helper/DataLayer/modalCrossSelling'
import { ProductHelper } from '#/global/helper/Product/ProductHelper'

interface Settings {
    hasGift: boolean
    seasonPoints: number
}

export const App = () => {
    // Define state
    const [isOpen, setIsOpen] = useState(false)
    const [productCode, setProductCode] = useState<string>()
    const [type, setType] = useState<'eshop' | 'rental'>()
    const [productQuantity, setProductQuantity] = useState<number>()

    /* rental Props */
    const [rentalDate, setRentalDate] = useState<{ from: Date, to: Date }>()
    const [hasUserAgreedWithGuaranteeOnInit, setHasUserAgreedWithGuaranteeOnInit] = useState<boolean>(false)
    const [hasUserAgreedWithGuarantee, setHasUserAgreedWithGuarantee] = useState<boolean>(false)
    const [typeOfRental, setTypeOfRental] = useState<AddToCartRentalParams['typeOfRental']>('Default')
    const [recordIdRentalList, setRecordIdRentalList] = useState<number[]>([])

    const isRental = type === 'rental'
    const lang = Config.getLang()

    // Get product data from api
    const product = useQuery({
        queryKey: ['InsertedProduct', productCode, isRental],
        queryFn: async () => {
            if (isRental) {
                const productList = await ServiceProduct.getProductRentalByCode(productCode!, ['detail', 'price', 'related'])
                return productList[0]
            }
            const productList = await ServiceProduct.getProductEshopByCode(productCode!, ['detail', 'price', 'availability', 'unit', 'related', 'season'])
            return productList[0]
        },
        enabled: !!productCode,
    })

    // Register event listener after initial render
    useEffect(() => {
        window.addEventListener('ES_ADD_TO_CART_ESHOP_AFTER', openModalByEshop)
        window.addEventListener('ES_ADD_TO_CART_RENTAL_AFTER', openModalByRental)
        return () => {
            window.removeEventListener('ES_ADD_TO_CART_ESHOP_AFTER', openModalByEshop)
            window.removeEventListener('ES_ADD_TO_CART_RENTAL_AFTER', openModalByRental)
        }
    })

    // Open modal by eshop
    const openModalByEshop = (event: CustomEvent<AddToCartEshopAfterParams>) => {
        const payload = event.detail

        // console.log only in development
        import.meta.env.DEV && console.log(payload, 'r-a-modal-cross-selling')

        if (!payload.code || !payload.quantitySales || !payload.unitSales || !payload.eventInvoker) {
            console.error(`Missing expected params in ES_ADD_TO_CART_ESHOP_AFTER payload.`, payload)
            return
        }

        if (payload.eventInvoker !== 'compact') {
            setProductCode(payload.code)
            setType('eshop')
            setProductQuantity(payload.quantitySales)

            setIsOpen(true)
        }
    }

    // Open modal by rental
    const openModalByRental = (event: CustomEvent<AddToCartRentalAfterParams>) => {
        const payload = event.detail

        // Log to a console only in development
        import.meta.env.DEV && console.log(payload, 'r-a-modal-cross-selling')

        // Skip, if product was not successfully added to cart
        if (!payload.isSuccess) {
            return
        }

        if (!payload.unionCode || !payload.quantitySales || payload.dateFrom === undefined || payload.dateTo === undefined || !payload.eventInvoker || !payload.recordId) {
            console.error(`Missing expected params in ES_ADD_TO_CART_RENTAL_AFTER payload.`, payload)
            return
        }

        // Push recordId, if an invoker is also "compact" (Guarantee must be added also to related products)
        setRecordIdRentalList([...recordIdRentalList, payload.recordId])

        // Check allowed invokers to open modal
        if (payload.eventInvoker !== 'compact') {
            setProductCode(payload.unionCode)
            setType('rental')
            setProductQuantity(payload.quantity)
            setRentalDate({ from: payload.dateFrom, to: payload.dateTo })
            setHasUserAgreedWithGuarantee(!!payload.withGuarantee)
            setHasUserAgreedWithGuaranteeOnInit(!!payload.withGuarantee)
            setTypeOfRental(payload.typeOfRental)
            setIsOpen(true)
        }
    }

    const closeModal = () => {
        document.querySelector<HTMLDivElement>('#wrap')?.classList.remove('blur')
        setIsOpen(false)
    }

    if (!isOpen) {
        return null
    }

    if (!productCode) {
        return null
    }

    if (!productQuantity) {
        return null
    }

    if (!product.isSuccess || !product.data) {
        return null
    }

    if (isRental && !rentalDate) {
        return null
    }

    const productCodeRelatedList = [...product.data.related]

    const settings: Settings = {
        hasGift: false,
        seasonPoints: 0,
    }

    // Season points and gift
    if (product.data.type === 'eshop') {
        settings.hasGift = product.data.detail.hasGift
        settings.seasonPoints = ProductHelper.calculateSeasonPoints(product.data, productQuantity)
    }

    const hasGift = settings.hasGift
    const hasSeasonPoints = settings.seasonPoints > 0
    const hasGiftOrSeasonPoints = hasGift || hasSeasonPoints
    const hasProductRelated = productCodeRelatedList.length > 0
    const hasProductGuarantee = isRental && recordIdRentalList.length > 0 && product.data.type === 'rental' && product.data.price.hasGuarantee && !hasUserAgreedWithGuaranteeOnInit
    const isMiddleContainerVisible = hasGiftOrSeasonPoints || hasProductRelated || hasProductGuarantee
    const isButtonsFooterVisible = !hasGiftOrSeasonPoints || hasProductRelated || hasProductGuarantee

    const t = i18n[lang]

    const buttons = (<div className={css.buttons}><Buttons onClose={closeModal} /></div>)

    hasProductRelated && initDataLayer()

    return (
        <Modal
            open={isOpen}
            title={t.title}
            footer={isButtonsFooterVisible ? buttons : undefined}
            onClose={closeModal}
            fullWidth>
            <ProductPreviewLine
                date={rentalDate}
                lang={lang}
                product={product.data}
                quantity={productQuantity}
                typeOfRental={typeOfRental}
            />

            {isMiddleContainerVisible && (
                <div className={`r-up-sale ${css.container}`}>
                    {buttons}
                    <div className={`${css.body} ${isRental ? css.rental : ''}`.trim()}>

                        {hasGift && (<Gift />)}

                        {hasSeasonPoints && (<SeasonPoints points={settings.seasonPoints} />)}

                        {hasProductGuarantee && product.data.type === 'rental' && product.data.price.hasGuarantee && ( // Redundant condition because of the type check => `product.data.type === 'rental' && product.data.price.hasGuarantee &&`
                            <RentalGuarantee
                                hasUserAgreedWithGuarantee={hasUserAgreedWithGuarantee}
                                price={product.data.price.priceVatGuarantee}
                                recordIdList={recordIdRentalList}
                                setHasUserAgreedWithGuarantee={setHasUserAgreedWithGuarantee}
                                unit={product.data.price.unitGuarantee}
                            />
                        )}

                        {hasProductRelated && isRental && rentalDate !== undefined && (
                            <RentalProductList
                                date={rentalDate}
                                hasUserAgreedWithGuarantee={hasUserAgreedWithGuarantee}
                                productCodeList={productCodeRelatedList}
                                typeOfRental={typeOfRental}
                            />
                        )}

                        {hasProductRelated && !isRental && (
                            <EshopProductList productCodeList={productCodeRelatedList} />
                        )}
                    </div>
                </div>
            )}
        </Modal>
    )
}
